// Use this when having to create complicated inputs. See /dev/components/inputs for details

import React from "react";
import PropTypes from "prop-types";
import isString from "lodash/isString";

import InputErrorMessage from "components/shared/InputErrorMessage";
import LabelContainer from "components/shared/LabelContainer";

const propTypes = {
  InputComponent: PropTypes.oneOf(["input", "textarea"]),
  autocapitalize: PropTypes.string,
  autocomplete: PropTypes.string,
  autocorrect: PropTypes.string,
  errorMessage: PropTypes.string,
  handleChange: PropTypes.func,
  iconClass: PropTypes.string,
  inputClass: PropTypes.string,
  inputId: PropTypes.string,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  max: PropTypes.string,
  min: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  step: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string
};

const defaultProps = {
  InputComponent: "input",
  autocapitalize: "off",
  autocomplete: "off",
  autocorrect: "off",
  errorMessage: null,
  handleChange: null,
  iconClass: null,
  inputClass: "input-lg",
  isDisabled: false,
  isRequired: false,
  placeholder: "",
  type: "text",
  value: ""
};

const Input = (props) => {
  const {
    InputComponent,
    autocapitalize,
    autocomplete,
    autocorrect,
    errorMessage,
    handleChange,
    inputClass,
    inputId = props.name, // Default input ID to the name
    isControlled,
    isDisabled,
    max,
    min,
    name,
    placeholder,
    step,
    type,
    value,
    ...labelProps
  } = props;

  const hasError = errorMessage !== null || props.hasError;
  const className = hasError ? `${inputClass} error` : inputClass;

  const hasIcon = isString(props.iconClass) && props.iconClass.length > 0;

  // If component is controlled externally, set isControlled to true and
  // be sure to pass a handleChange as well
  const valueProps = {};
  if (isControlled) {
    valueProps.value = value;
  } else {
    valueProps.defaultValue = value;
  }

  const onChange = (e) => {
    handleChange && handleChange(e);
  };

  return (
    <div className={`input-container ${hasIcon && "input-container--icon"}`}>
      {hasIcon && <i className={props.iconClass} />}
      <LabelContainer {...labelProps} hasError={hasError} name={inputId} />
      <InputComponent
        autoCapitalize={autocapitalize}
        autoComplete={autocomplete}
        autoCorrect={autocorrect}
        className={className}
        disabled={isDisabled}
        id={inputId}
        max={max}
        min={min}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        step={step}
        type={type}
        {...valueProps}
      />
      {errorMessage && <InputErrorMessage message={errorMessage} />}
    </div>
  );
};

Input.defaultProps = defaultProps;
Input.propTypes = propTypes;

export default Input;
